import { useInfiniteQuery } from '@tanstack/react-query';
import { UseResearchLibraryOptions } from './types';
import { ResearchQueryService } from './research-query-service';

// Create stable query key that excludes undefined values - optimized for performance
const createQueryKey = (options: UseResearchLibraryOptions) => {
  // Create a stable key by only including defined values and using consistent ordering
  const keyParts: (string | number | boolean)[] = ['research-library-list'];
  
  // Add options in consistent order to ensure stable keys
  if (options.searchQuery) keyParts.push('search', options.searchQuery);
  if (options.researchType) keyParts.push('type', options.researchType);
  if (options.showFavorites) keyParts.push('favorites');
  if (options.scope && options.scope !== 'all') keyParts.push('scope', options.scope);
  if (options.timeframe) keyParts.push('timeframe', options.timeframe);
  if (options.country) keyParts.push('country', options.country);
  if (options.sourceQuality) keyParts.push('quality', options.sourceQuality);
  if (options.typeSpecificFilters && Object.keys(options.typeSpecificFilters).length > 0) {
    keyParts.push('filters', JSON.stringify(options.typeSpecificFilters));
  }
  if (options.pageSize && options.pageSize !== 12) keyParts.push('size', options.pageSize);
  
  return keyParts;
};

export const useResearchLibraryQuery = (options: UseResearchLibraryOptions = {}) => {
  const { pageSize = 12 } = options;

  return useInfiniteQuery({
    queryKey: createQueryKey(options),
    queryFn: ({ pageParam = 0 }) => {
      // Execute query without debug logging for better performance
      return ResearchQueryService.fetchResearchLibrary(options, pageParam);
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Enable background loading - don't block page render
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    refetchOnMount: true, // Load data when component mounts
    // Allow skeleton to show while loading
    notifyOnChangeProps: ['data', 'error'], // Only notify on data/error changes, not loading states
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('not authenticated')) {
        return false;
      }
      
      // Don't retry on database schema errors  
      if (error?.code === '42883' || error?.message?.includes('operator does not exist')) {
        return false;
      }
      
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
